import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { useContext, useEffect, useState } from "react"
import { FiRefreshCw, FiTerminal } from "react-icons/fi"

import { ReadersService } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { OrganizationContext } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"

export const Route = createFileRoute("/_layout/debug")({
  component: DebugPage,
})

function DebugPage() {
  const showToast = useCustomToast()
  const { currentOrganization } = useContext(OrganizationContext)
  const [readers, setReaders] = useState<any[]>([])
  const [selectedReaderId, setSelectedReaderId] = useState<string>("")
  const [commands, setCommands] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!currentOrganization) return
    ReadersService.readOrganizationReaders({
      organisationId: currentOrganization.id,
    })
      .then((data: any) => {
        const list = Array.isArray(data) ? data : (data?.data ?? [])
        setReaders(list)
        if (list.length > 0) setSelectedReaderId(list[0].id)
      })
      .catch(() => {})
  }, [currentOrganization])

  useEffect(() => {
    if (selectedReaderId) fetchCommands()
  }, [selectedReaderId])

  const fetchCommands = async () => {
    if (!currentOrganization || !selectedReaderId) return
    setLoading(true)
    const token = localStorage.getItem("access_token")
    const base = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "")
    const url = `${base}/api/v1/organizations/${currentOrganization.id}/readers/${selectedReaderId}/commands?limit=50`
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setCommands(data)
    } catch (e: any) {
      showToast.showErrorToast(`Failed to load commands: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Text color="gray.400">
          Please select an organization from the sidebar.
        </Text>
      </VisionCard>
    )
  }

  const statusColor: Record<string, string> = {
    COMPLETED: "green.400",
    FAILED: "red.400",
    TIMEOUT: "orange.400",
    RUNNING: "blue.400",
    PENDING: "gray.400",
  }

  return (
    <Box>
      <Flex align="center" justify="space-between" mb={8}>
        <Heading size="xl" color="white">
          Debug — Command Log
        </Heading>
        <HStack gap={3}>
          {readers.length > 0 && (
            <select
              value={selectedReaderId}
              onChange={(e) => setSelectedReaderId(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "8px",
                color: "white",
                padding: "6px 12px",
                fontSize: "14px",
              }}
            >
              {readers.map((r: any) => (
                <option
                  key={r.id}
                  value={r.id}
                  style={{ background: "#1a1f37" }}
                >
                  {r.name} ({r.mac_address})
                </option>
              ))}
            </select>
          )}
          <VisionButton
            variant="dark"
            size="sm"
            onClick={fetchCommands}
            loading={loading}
          >
            <FiRefreshCw /> Refresh
          </VisionButton>
        </HStack>
      </Flex>

      <VisionCard>
        {loading && commands.length === 0 && (
          <Flex justify="center" py={8}>
            <Spinner color="white" />
          </Flex>
        )}

        {!loading && commands.length === 0 && (
          <Flex direction="column" align="center" py={8}>
            <IconBox variant="glass" size="lg" mb={4}>
              <FiTerminal fontSize="24px" color="white" />
            </IconBox>
            <Text color="gray.400">No commands yet.</Text>
          </Flex>
        )}

        <VStack gap={2} align="stretch" maxH="700px" overflowY="auto">
          {commands.map((cmd: any) => {
            const color = statusColor[cmd.status] ?? "gray.400"
            return (
              <Box
                key={cmd.id}
                p={3}
                borderRadius="8px"
                bg="rgba(0,0,0,0.25)"
                border="1px solid rgba(255,255,255,0.08)"
                fontSize="xs"
                fontFamily="mono"
              >
                <HStack justify="space-between" mb={1}>
                  <HStack gap={2}>
                    <Text color="white" fontWeight="bold">
                      {cmd.command_type ?? "unknown"}
                    </Text>
                    <Text color={color}>{cmd.status}</Text>
                  </HStack>
                  <Text color="gray.500">
                    {new Date(cmd.timestamp).toLocaleTimeString()}
                  </Text>
                </HStack>
                {cmd.command_data?.command_str && (
                  <Text color="gray.300" mb={1}>
                    cmd: {cmd.command_data.command_str}
                  </Text>
                )}
                {cmd.command_id && (
                  <Text color="gray.500">id: {cmd.command_id}</Text>
                )}
                {cmd.result && (
                  <Box
                    mt={2}
                    p={2}
                    bg="rgba(0,0,0,0.3)"
                    borderRadius="4px"
                    maxH="120px"
                    overflowY="auto"
                  >
                    <Text color="gray.400" whiteSpace="pre-wrap">
                      {JSON.stringify(cmd.result, null, 2)}
                    </Text>
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      </VisionCard>
    </Box>
  )
}
